import React from 'react';
import { Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface OptionCardProps {
  selected: boolean;
  onSelect: () => void;
  icon: LucideIcon;
  title: string;
  description?: string;
  /** Short qualifier shown beside the title, e.g. "Recommended". */
  badge?: string;
  disabled?: boolean;
  /** Replaces the description while disabled, to explain why. */
  disabledReason?: string;
  /** `row` puts the icon beside the text; `stacked` puts it above. */
  layout?: 'row' | 'stacked';
}

/**
 * The single selection control for the wizard.
 *
 * Selection is always the accent color regardless of what is being chosen —
 * the previous flow tinted each choice differently (green for home, blue for
 * office, amber for "same as start"), which made "selected" something the user
 * had to re-learn on every screen.
 */
export const OptionCard: React.FC<OptionCardProps> = ({
  selected,
  onSelect,
  icon: Icon,
  title,
  description,
  badge,
  disabled = false,
  disabledReason,
  layout = 'stacked',
}) => {
  const body = disabled && disabledReason ? disabledReason : description;

  return (
    // eslint-disable-next-line no-restricted-syntax -- large selectable card with custom internal layout; no ui/ primitive covers this
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled}
      className={`relative w-full rounded-xl border-2 p-3.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40 ${
        selected
          ? 'border-accent-500 bg-accent-50'
          : disabled
            ? 'cursor-not-allowed border-ink-100 bg-ink-50'
            : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50'
      }`}
    >
      <div className={layout === 'row' ? 'flex items-center gap-3' : ''}>
        <div
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
            layout === 'stacked' ? 'mb-2.5' : ''
          } ${
            selected
              ? 'bg-accent-500 text-white'
              : disabled
                ? 'bg-ink-100 text-ink-300'
                : 'bg-ink-100 text-ink-500'
          }`}
        >
          <Icon className="h-4.5 w-4.5" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pr-6">
            <span
              className={`text-sm font-semibold ${disabled ? 'text-ink-400' : 'text-ink-900'}`}
            >
              {title}
            </span>
            {badge && (
              <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-2xs font-semibold text-accent-700">
                {badge}
              </span>
            )}
          </div>
          {body && (
            <p className={`mt-0.5 text-xs ${disabled ? 'text-ink-400' : 'text-ink-500'}`}>
              {body}
            </p>
          )}
        </div>
      </div>

      {selected && (
        <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-white">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
      )}
    </button>
  );
};
